import { createRoute } from '@tanstack/react-router';
import { Suspense } from 'react';
import { RoutesMap } from '@Router/router.types';
import { routesModel } from '@Router/router.consts';
import { beforeLoadGeneral } from '@Router/middleware';

import { GeneralSpinner } from '@Components/generalSpinner';
import { EddAdditionalBusinessLocations } from '@Pages/happyFlowPages/eddQuestions/pages/direct/eddAdditionalBusinessLocations/EddAdditionalBusinessLocations';
import { EddInternationalActivity } from '@Pages/happyFlowPages/eddQuestions/pages/direct/edd-international-activity/EddInternationalActivity';
import { EddBusinessSize } from '@Pages/happyFlowPages/eddQuestions/pages/direct/edd-business-size/EddBusinessSize';
import { EddAnnualRevenue } from '@Pages/happyFlowPages/eddQuestions/pages/direct/edd-annual-revenue/EddAnnualRevenue';
import { EddIntendedUse } from '@Pages/happyFlowPages/eddQuestions/pages/direct/edd-intended-use/EddIntendedUse';
import { EddWebsiteSocial } from '@Pages/happyFlowPages/eddQuestions/pages/direct/edd-website-social/EddWebsiteSocial';
import { EddBusinessCapital } from '@Pages/happyFlowPages/eddQuestions/pages/direct/edd-business-capital/EddBusinessCapital';
import { EddFundingSources } from '@Pages/happyFlowPages/eddQuestions/pages/direct/edd-funding-sources/EddFundingSources';
import { EddNonprofitFundingSources } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-nonprofit-funding-sources/EddNonprofitFundingSources';
import { EddNonprofitDonors } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-nonprofit-donors/EddNonprofitDonors';
import { EddNonprofitOrganizationSize } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-nonprofit-organization-size/EddNonprofitOrganizationSize';
import { EddNonprofitMonthlyActivity } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-nonprofit-monthly-activity/EddNonprofitMonthlyActivity';
import { EddNonprofitAffiliation } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-nonprofit-affiliation/EddNonprofitAffiliation';
import { EddNonprofitSocialMedia } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-nonprofit-social-media/EddNonprofitSocialMedia';
import { EddOrganizationRole } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-organization-role/EddOrganizationRole';
import { EddNonprofitDescription } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-nonprofit-description/EddNonprofitDescription';
import { EddNonprofitDescriptionB } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-nonprofit-description-b/EddNonprofitDescriptionB';
import { EddNonprofitDescriptionC } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-nonprofit-description-c/EddNonprofitDescriptionC';
import { EddNonprofitDescriptionD } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-nonprofit-description-d/EddNonprofitDescriptionD';
import { EddNonprofitDescriptionE } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-nonprofit-description-e/EddNonprofitDescriptionE';
import { EddBeneficiarySelection } from '@Pages/happyFlowPages/eddQuestions/pages/non-profit/edd-beneficiary-selection/EddBeneficiarySelection';
import { EddSanctionedCountries } from '@Pages/happyFlowPages/eddQuestions/pages/additional-risk/edd-sanctioned-countries/EddSanctionedCountries';
import { EddCharities } from '@Pages/happyFlowPages/eddQuestions/pages/additional-risk/edd-charities/EddCharities';
import { EddMilitaryPreciousMetals } from '@Pages/happyFlowPages/eddQuestions/pages/additional-risk/edd-military-precious-metals/EddMilitaryPreciousMetals';
import { EddCashPayments } from '@Pages/happyFlowPages/eddQuestions/pages/additional-risk/edd-cash-payments/EddCashPayments';
import { EddTaxLegalServices } from '@Pages/happyFlowPages/eddQuestions/pages/additional-risk/edd-tax-legal-services/EddTaxLegalServices';
import { EddFundTransfer } from '@Pages/happyFlowPages/eddQuestions/pages/additional-risk/edd-fund-transfer/EddFundTransfer';
import { EddInvestmentCustodial } from '@Pages/happyFlowPages/eddQuestions/pages/additional-risk/edd-investment-custodial/EddInvestmentCustodial';
import { rootRoute } from './rootRoute';

const EddAdditionalBusinessLocationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_ADDITIONAL_BUSINESS_LOCATIONS].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_ADDITIONAL_BUSINESS_LOCATIONS,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddAdditionalBusinessLocations />
    </Suspense>
  ),
});

const EddInternationalActivityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_INTERNATIONAL_ACTIVITY].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_INTERNATIONAL_ACTIVITY,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddInternationalActivity />
    </Suspense>
  ),
});

const EddBusinessSizeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_BUSINESS_SIZE].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_BUSINESS_SIZE,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddBusinessSize />
    </Suspense>
  ),
});

const EddAnnualRevenueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_ANNUAL_REVENUE].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_ANNUAL_REVENUE,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddAnnualRevenue />
    </Suspense>
  ),
});

const EddIntendedUseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_INTENDED_USE].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_INTENDED_USE,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddIntendedUse />
    </Suspense>
  ),
});

const EddWebsiteSocialRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_WEBSITE_SOCIAL].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_WEBSITE_SOCIAL,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddWebsiteSocial />
    </Suspense>
  ),
});

const EddBusinessCapitalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_BUSINESS_CAPITAL].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_BUSINESS_CAPITAL,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddBusinessCapital />
    </Suspense>
  ),
});

const EddFundingSourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_FUNDING_SOURCES].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_FUNDING_SOURCES,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddFundingSources />
    </Suspense>
  ),
});

const EddNonprofitFundingSourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_NONPROFIT_FUNDING_SOURCES].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_NONPROFIT_FUNDING_SOURCES,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddNonprofitFundingSources />
    </Suspense>
  ),
});

const EddNonprofitDonorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_NONPROFIT_DONORS].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_NONPROFIT_DONORS,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddNonprofitDonors />
    </Suspense>
  ),
});

const EddNonprofitOrganizationSizeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_NONPROFIT_ORGANIZATION_SIZE].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_NONPROFIT_ORGANIZATION_SIZE,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddNonprofitOrganizationSize />
    </Suspense>
  ),
});

const EddNonprofitMonthlyActivityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_NONPROFIT_MONTHLY_ACTIVITY].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_NONPROFIT_MONTHLY_ACTIVITY,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddNonprofitMonthlyActivity />
    </Suspense>
  ),
});

const EddNonprofitAffiliationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_NONPROFIT_AFFILIATION].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_NONPROFIT_AFFILIATION,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddNonprofitAffiliation />
    </Suspense>
  ),
});

const EddNonprofitSocialMediaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_NONPROFIT_SOCIAL_MEDIA].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_NONPROFIT_SOCIAL_MEDIA,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddNonprofitSocialMedia />
    </Suspense>
  ),
});

const EddOrganizationRoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_ORGANIZATION_ROLE].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_ORGANIZATION_ROLE,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddOrganizationRole />
    </Suspense>
  ),
});

const EddNonProfitDescriptionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_NONPROFIT_DESCRIPTION].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_NONPROFIT_DESCRIPTION,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddNonprofitDescription />
    </Suspense>
  ),
});

const EddNonProfitDescriptionBRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_NONPROFIT_DESCRIPTION_B].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_NONPROFIT_DESCRIPTION_B,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddNonprofitDescriptionB />
    </Suspense>
  ),
});

const EddNonProfitDescriptionCRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_NONPROFIT_DESCRIPTION_C].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_NONPROFIT_DESCRIPTION_C,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddNonprofitDescriptionC />
    </Suspense>
  ),
});

const EddNonProfitDescriptionDRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_NONPROFIT_DESCRIPTION_D].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_NONPROFIT_DESCRIPTION_D,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddNonprofitDescriptionD />
    </Suspense>
  ),
});

const EddNonProfitDescriptionERoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_NONPROFIT_DESCRIPTION_E].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_NONPROFIT_DESCRIPTION_E,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddNonprofitDescriptionE />
    </Suspense>
  ),
});

const EddBeneficiarySelectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_BENEFICIARY_SELECTION].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_BENEFICIARY_SELECTION,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.BUSINESS_TYPE_SELECTION,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddBeneficiarySelection />
    </Suspense>
  ),
});

const EddSanctionedCountriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_SANCTIONED_COUNTRIES].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_SANCTIONED_COUNTRIES,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddSanctionedCountries />
    </Suspense>
  ),
});

const EddCharitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_CHARITIES].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_CHARITIES,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddCharities />
    </Suspense>
  ),
});

const EddMilitaryPreciousMetalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_MILITARY_PRECIOUS_METALS].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_MILITARY_PRECIOUS_METALS,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddMilitaryPreciousMetals />
    </Suspense>
  ),
});

const EddCashPaymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_CASH_PAYMENTS].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_CASH_PAYMENTS,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddCashPayments />
    </Suspense>
  ),
});

const EddTaxLegalServicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_TAX_LEGAL_SERVICES].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_TAX_LEGAL_SERVICES,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddTaxLegalServices />
    </Suspense>
  ),
});

const EddFundTransferRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_FUND_TRANSFER].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_FUND_TRANSFER,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddFundTransfer />
    </Suspense>
  ),
});

const EddInvestmentCustodialRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.EDD_INVESTMENT_CUSTODIAL].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.EDD_INVESTMENT_CUSTODIAL,
      shouldUpdateServerNavStack: true,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <EddInvestmentCustodial />
    </Suspense>
  ),
});

export const eddRoutes = [
  EddAdditionalBusinessLocationsRoute,
  EddInternationalActivityRoute,
  EddBusinessSizeRoute,
  EddAnnualRevenueRoute,
  EddIntendedUseRoute,
  EddWebsiteSocialRoute,
  EddBusinessCapitalRoute,
  EddFundingSourcesRoute,
  EddNonprofitFundingSourcesRoute,
  EddNonprofitDonorsRoute,
  EddNonprofitOrganizationSizeRoute,
  EddNonprofitMonthlyActivityRoute,
  EddNonprofitAffiliationRoute,
  EddNonprofitSocialMediaRoute,
  EddOrganizationRoleRoute,
  EddNonProfitDescriptionRoute,
  EddNonProfitDescriptionBRoute,
  EddNonProfitDescriptionCRoute,
  EddNonProfitDescriptionDRoute,
  EddNonProfitDescriptionERoute,
  EddBeneficiarySelectionRoute,
  EddSanctionedCountriesRoute,
  EddCharitiesRoute,
  EddMilitaryPreciousMetalsRoute,
  EddCashPaymentsRoute,
  EddTaxLegalServicesRoute,
  EddFundTransferRoute,
  EddInvestmentCustodialRoute,
];
